import type { PersonaCore } from './synthetic.ts';
import { legacyLevels, resolveTraits, type LevelRange } from './persona-traits.ts';

/**
 * The kind of site, read from its pages before the first test: it decides which ready-made visitors we
 * suggest. A rough guess on purpose: the person sees it ("We read your site: an online shop") and can
 * change it.
 */
export const SITE_KINDS = ['shop', 'software', 'services', 'content', 'other'] as const;
export type SiteKind = (typeof SITE_KINDS)[number];

/** "We read your site: …" */
export const siteKindLabel: Record<SiteKind, string> = {
  shop: 'an online shop',
  software: 'a software product',
  services: 'a business offering services',
  content: 'a blog or magazine',
  other: 'a website',
};

/** The filter of the persona library and the "good for" of a persona. */
export const siteKindPlural: Record<SiteKind, string> = {
  shop: 'Online shops',
  software: 'Software',
  services: 'Services',
  content: 'Blogs and magazines',
  other: 'Any site',
};

const SIGNALS: Record<Exclude<SiteKind, 'other'>, RegExp[]> = {
  shop: [
    /add to (cart|bag|basket)/g,
    /\b(cart|basket|checkout|shipping|in stock|out of stock|wishlist|free returns|buy now|shop now|order now)\b/g,
    /\b(carrello|aggiungi al carrello|spedizion[ei]|acquista|resi gratuiti|disponibilit[àa])\b/g,
  ],
  software: [
    /\b(free trial|start (your )?free|sign up free|pricing|per (user|seat)|\/month|api|integrations?|dashboard|workspace|saas|platform|onboarding|log ?in)\b/g,
    /\b(prova gratuita|piattaform[ae]|abbonamento|al mese|integrazion[ei])\b/g,
  ],
  services: [
    /\b(book (an? )?(appointment|table|call|now)|opening hours|our services|get a quote|contact us|reservations?|clinic|studio|agency|consultation)\b/g,
    /\b(prenota|orari|i nostri servizi|preventivo|contattaci|appuntamento|studio|agenzia)\b/g,
  ],
  content: [
    /\b(read more|latest (posts|articles|news)|subscribe to (our|the) newsletter|minutes? read|by [A-Z][a-z]+ [A-Z][a-z]+|blog|magazine|podcast)\b/g,
    /\b(leggi (di più|tutto)|ultimi articoli|iscriviti alla newsletter|minuti di lettura)\b/g,
  ],
};

/** The kind of a site from the text of its pages; "other" when no kind stands out. */
export function siteKindFrom(text: string): SiteKind {
  const lower = text.toLowerCase();
  let best: SiteKind = 'other';
  let bestScore = 2;
  for (const kind of Object.keys(SIGNALS) as (keyof typeof SIGNALS)[]) {
    const score = SIGNALS[kind].reduce((n, re) => n + (lower.match(re)?.length ?? 0), 0);
    if (score > bestScore) {
      best = kind;
      bestScore = score;
    }
  }
  return best;
}

/** How hard a ready-made visitor is on a site: the library shows it, the first test uses gentle ones. */
export const PERSONA_TEMPERS = ['gentle', 'balanced', 'demanding'] as const;
export type PersonaTemper = (typeof PERSONA_TEMPERS)[number];
export const personaTemperLabel: Record<PersonaTemper, string> = { gentle: 'Gentle', balanced: 'Balanced', demanding: 'Demanding' };

/**
 * The three ready-made visitors of a first test, by kind of site: permissive, everyday people who give the site
 * every chance, on a phone, a computer and (where it fits) a tablet. What stops them is a real problem.
 */
export const GENTLE_PICKS: Record<SiteKind, readonly [string, string, string]> = {
  shop: ['curious-browser', 'loyal-fan', 'friendly-retiree'],
  software: ['early-adopter', 'colleague-referral', 'feedback-friend'],
  services: ['local-neighbour', 'loyal-fan', 'friendly-retiree'],
  content: ['long-read-fan', 'curious-browser', 'happy-subscriber'],
  other: ['feedback-friend', 'returning-customer', 'friendly-retiree'],
};

/** "Run again, tougher": impatient, skeptical, and on a small screen with older eyes. */
export const TOUGH_PICKS = ['skeptical-first-timer', 'busy-professional', 'senior-mobile'] as const;

const atLeast = (r: LevelRange, n: number): LevelRange => ({ min: Math.max(r.min, n), max: Math.max(r.max, n) });
const atMost = (r: LevelRange, n: number): LevelRange => ({ min: Math.min(r.min, n), max: Math.min(r.max, n) });

/**
 * The persona as a gentle visitor, for a first test: patient (3 or more), trusting enough (trust 3 or
 * less: 1 trusts easily), reading more than skimming, no reason to leave at once, no tight time limit.
 * Still a real person: a broken button or a missing price stops them all the same.
 */
export function gentleCore(core: PersonaCore): PersonaCore {
  const t = resolveTraits(core);
  const traits = {
    ...t,
    patience: atLeast(t.patience, 3),
    trust: atMost(t.trust, 3),
    reading: atLeast(t.reading, 3),
    timeMinutes: null,
    dealBreakers: [],
    customDealBreakers: [],
  };
  return { ...core, traits, ...legacyLevels(traits) };
}
